import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { ArrowRight, Award, PlayCircle, Sparkles, Users, Zap, CheckCircle2 } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { Moon, Sun } from "lucide-react";

const FEATURES = [
  { icon: PlayCircle, title: "Cinematic Lessons", desc: "HD video lessons with premium production quality." },
  { icon: Award, title: "Verified Certificates", desc: "QR-verifiable certificates on every course completion." },
  { icon: Users, title: "Live Cohorts", desc: "Weekly live sessions with Prerna Maheshwari." },
  { icon: Zap, title: "AI-Ready Curriculum", desc: "Learn to earn with AI, automation and modern tools." },
];

const CATEGORIES = [
  "Digital Marketing", "AI Mastery", "Business Automation", "Video Editing", "Canva Design",
  "Instagram Growth", "Facebook Ads", "Google Ads", "SEO", "WordPress",
  "Email Marketing", "Affiliate", "Freelancing", "Entrepreneurship", "Communication", "Mindset",
];

export default function Landing() {
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3.5">
          <Logo size={38} />
          <div className="flex items-center gap-3">
            <button onClick={toggle} data-testid="landing-theme-toggle"
              className="h-9 w-9 rounded-full flex items-center justify-center bg-secondary/60">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">Sign in</Button></Link>
            <Link to="/register">
              <Button data-testid="landing-get-started-btn" className="rounded-full royal-gradient border-0 text-white shadow-[0_10px_30px_-10px_rgba(11,27,58,0.6)] btn-lift">
                Get started <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 relative royal-radial">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-in-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3.5 py-1.5 text-xs font-semibold tracking-wider text-gold uppercase">
              <Sparkles className="h-3.5 w-3.5" /> Channel Partner – NVDigitalEdu
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-foreground">
              Build Skills. <br />
              Create <span className="italic text-magenta">Opportunities.</span><br />
              Transform Your <span className="text-teal">Career.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-lg leading-relaxed">
              Learn <span className="font-semibold text-foreground">Digital Marketing, AI, Automation, Freelancing and Entrepreneurship</span> through practical, structured learning.
            </p>
            <p className="mt-3 text-sm text-muted-foreground max-w-lg">
              Founded by <span className="font-semibold text-foreground">Prerna Maheshwari</span> — IIM-certified Career Transformation Coach.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-primary"
                  className="rounded-full royal-gradient border-0 text-white h-12 px-7 shadow-[0_20px_50px_-15px_rgba(11,27,58,0.6)] btn-lift">
                  Start learning free <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" data-testid="hero-cta-secondary"
                  className="rounded-full h-12 px-7 border-foreground/20 btn-lift">
                  I have an account
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm">
              {["16 Premium Courses", "Live Cohorts", "Verified Certificates"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-teal" /> {t}
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative animate-in-up">
            <div className="absolute -inset-6 bg-gold/20 blur-3xl rounded-[3rem]" />
            <div className="relative rounded-[2rem] overflow-hidden border border-gold/30 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1483808161634-29aa1b0e585e"
                   alt="student learning" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4 flex items-center gap-3">
                <div className="h-12 w-12 rounded-full gold-gradient flex items-center justify-center">
                  <PlayCircle className="h-6 w-6 text-[#1E3A8A]" />
                </div>
                <div>
                  <p className="font-semibold">Digital Marketing Masterclass</p>
                  <p className="text-xs text-muted-foreground">42 hrs · Prerna Maheshwari</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-xs text-muted-foreground">Rating</p>
                  <p className="font-bold text-gold">4.9 ★</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature bento */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title}
                 className="group relative overflow-hidden rounded-3xl border border-border/60 bg-card p-6 card-hover"
                 style={{ animationDelay: `${i * 60}ms` }}>
              <div className="h-11 w-11 rounded-2xl gold-gradient flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-[#1E3A8A]" />
              </div>
              <h3 className="font-display text-xl font-semibold">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 px-6 border-t border-border/60">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Curriculum</p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
                16 premium programs. One <span className="text-primary">career transformation</span>.
              </h2>
            </div>
            <Link to="/register" className="hidden sm:block">
              <Button variant="outline" className="rounded-full border-foreground/20">Explore all</Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {CATEGORIES.map((c) => (
              <div key={c}
                   className="rounded-2xl border border-border/60 bg-card px-4 py-4 text-sm font-medium hover:border-gold/50 hover:text-gold transition">
                {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto rounded-[2rem] royal-gradient p-10 sm:p-14 relative overflow-hidden grain">
          <div className="absolute -top-10 -right-10 h-60 w-60 rounded-full bg-teal/40 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-magenta/30 blur-3xl" />
          <div className="relative">
            <h3 className="font-display text-white text-4xl sm:text-5xl font-bold tracking-tight">
              Your career upgrade <br /> starts <span className="text-gold">tonight.</span>
            </h3>
            <p className="text-white/80 mt-3 max-w-xl">
              Practical, structured learning in Digital Marketing, AI, Automation, Freelancing & Entrepreneurship.
            </p>
            <Link to="/register">
              <Button size="lg" data-testid="footer-cta"
                className="mt-8 rounded-full bg-white text-primary hover:bg-white/90 h-12 px-7 btn-lift font-semibold">
                Create free account <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/60 py-8 px-6 text-center text-xs text-muted-foreground">
        <p className="font-semibold text-foreground">InfiniteSkills Learning Portal</p>
        <p className="mt-1">Channel Partner – NVDigitalEdu</p>
        <p className="mt-1">Founded by Prerna Maheshwari</p>
      </footer>
    </div>
  );
}
